import {Button, Card, Checkbox, FormControl, TextInput} from 'platform/components';
import {Box, Hide, HStack, Show, VStack} from 'platform/foundation';

import {UseFormReturn, useWatch} from 'react-hook-form';

import {isNil} from 'ramda';

import {AddrDetailApiResponse} from '@dms/api/core';
import i18n from '@dms/i18n';
import {AddressSearch, useAddress} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId, useBoolean} from 'shared/web';

import {RentVehicleFormValues} from '../types/RentVehicleFormValues';
import {convertToCommonAddress} from '../utils/convertToCommonAddress';

interface RentAndReturnAddressProps extends RequiredTestIdProps {
  control: FormControl<RentVehicleFormValues>;
  formApi: UseFormReturn<RentVehicleFormValues>;
}

export function RentAndReturnAddress(props: RentAndReturnAddressProps) {
  const [rentAddress, returnAddress, isAddressSame] = useWatch({
    control: props.control,
    name: ['rentAddress', 'returnAddress', 'isAddressSame'],
  });

  const {composeAddress} = useAddress();

  const [isEditingRentAddress, editRentAddress, stopEditingRentAddress] = useBoolean(
    isNil(rentAddress)
  );
  const [isEditingReturnAddress, editReturnAddress, stopEditingReturnAddress] = useBoolean(
    isNil(returnAddress)
  );

  const rentAddressDisplay = composeAddress(rentAddress);
  const returnAddressDisplay = composeAddress(returnAddress);

  const handleSelectRentAddress = (address: AddrDetailApiResponse) => {
    props.formApi.setValue('rentAddress', convertToCommonAddress(address));
    stopEditingRentAddress();
  };
  const handleSelectReturnAddress = (address: AddrDetailApiResponse) => {
    props.formApi.setValue('returnAddress', convertToCommonAddress(address));
    stopEditingReturnAddress();
  };

  return (
    <Card variant="inlineGrey" title={i18n.t('page.rentalOrder.labels.rentReturnAddress')}>
      <VStack spacing={4}>
        <HStack align="flex-end" spacing={4}>
          <Show when={isEditingRentAddress}>
            <Box flex={1}>
              <AddressSearch
                onSelect={handleSelectRentAddress}
                data-testid={suffixTestId('rentAddressSearch', props)}
              />
            </Box>

            <Hide when={isNil(rentAddress)}>
              <Button
                variant="dangerGhost"
                title={i18n.t('general.actions.resetOverrides')}
                onClick={stopEditingRentAddress}
                data-testid={suffixTestId('cancelRentAddress', props)}
              />
            </Hide>
          </Show>

          <Hide when={isEditingRentAddress}>
            <Box flex={1}>
              <TextInput
                value={rentAddressDisplay ?? ''}
                label={i18n.t('page.rentalOrder.labels.rentAddress')}
                isRequired
                isDisabled
                data-testid={suffixTestId('rentAddress', props)}
              />
            </Box>

            <Button
              variant="secondary"
              title={i18n.t('general.actions.edit')}
              onClick={editRentAddress}
              data-testid={suffixTestId('editRentAddress', props)}
            />
          </Hide>
        </HStack>

        <Hide when={isAddressSame}>
          <HStack align="flex-end" spacing={4}>
            <Show when={isEditingReturnAddress}>
              <Box flex={1}>
                <AddressSearch
                  onSelect={handleSelectReturnAddress}
                  data-testid={suffixTestId('returnAddressSearch', props)}
                />
              </Box>

              <Hide when={isNil(returnAddress)}>
                <Button
                  variant="dangerGhost"
                  title={i18n.t('general.actions.resetOverrides')}
                  onClick={stopEditingReturnAddress}
                  data-testid={suffixTestId('cancelReturnAddress', props)}
                />
              </Hide>
            </Show>

            <Hide when={isEditingReturnAddress}>
              <Box flex={1}>
                <TextInput
                  value={returnAddressDisplay ?? ''}
                  label={i18n.t('page.rentalOrder.labels.returnAddress')}
                  isRequired
                  isDisabled
                  data-testid={suffixTestId('returnAddress', props)}
                />
              </Box>

              <Button
                variant="secondary"
                title={i18n.t('general.actions.edit')}
                onClick={editReturnAddress}
                data-testid={suffixTestId('editReturnAddress', props)}
              />
            </Hide>
          </HStack>
        </Hide>

        <Checkbox
          value={isAddressSame}
          onChange={(value) => props.formApi.setValue('isAddressSame', value)}
          label={i18n.t('page.rentalOrder.labels.sameAddress')}
          data-testid={suffixTestId('isAddressSame', props)}
        />
      </VStack>
    </Card>
  );
}
